<template>
  <div id="AccountScheduledReportsEdit" class="Main-Content">
    <ValidationObserver ref="validation" v-slot="{ invalid }">

      <Page
        :title="$t('scheduledReports.editScheduledReport')"
        :primaryAction="{
          label: $t('Save'),
          onAction: () => saveReport(),
          disabled: invalid || cantSave
        }"
        :breadcrumbs="hasChanged ? [
          {
            label: $t('Cancel'),
            onAction:() => cancel()
          }
        ] : [
          {
            label: $t('Back to list'),
            onAction: () => cancel()
          }
        ]"
        fullWidth
      >
        <Layout v-if="!loading">
          <LayoutSection>
            <Card>
              <CardSection>
                <FormLayout>
                  <ValidationProvider :name="$t('Name')" rules="required" v-slot="{ errors }">
                    <TextField
                      :label="$t('Name')"
                      v-model.trim="currentReport.name"
                      :readonly="readonly"
                      :error="errors[0]"
                      :help-text="`${$t('scheduledReports.exampleFileName')}: ${theUser && theUser.accountName}_2021-01-01_2021-01-02_${currentReport.name || $t('Name')}_xxxxxx.csv`"
                    />
                  </ValidationProvider>
                  <EmailListInput
                    :emails.sync="currentReport.recipients"
                    :label="$t('scheduledReports.recipients')"
                    :array-mode="true"
                  />
                  <Checkbox
                    v-if="theUser.isSuper || currentReport.addToFtp"
                    :label="$t('scheduledReports.addToFtp')"
                    v-model="currentReport.addToFtp"
                    :readonly="readonly || !theUser.isSuper"
                  />
                  <FormLayoutGroup>
                    <ValidationProvider :name="$t('scheduledReports.frequency')" rules="required" v-slot="{ errors }">
                      <Select
                        :label="$t('scheduledReports.frequency')"
                        :placeholder="$t('scheduledReports.selectFrequency')"
                        :options="periodOptions"
                        v-model="currentReport.period"
                        :readonly="readonly"
                        :error="errors[0]"
                      />
                    </ValidationProvider>
                    <ValidationProvider :name="$t('scheduledReports.scheduleStartDate')" rules="required" v-slot="{ errors }">
                      <DatePicker
                        :label="$t('scheduledReports.scheduleStartDate')"
                        :placeholder="$t('Choose a date')"
                        v-model="currentReport.scheduleStartDate"
                        :readonly="readonly"
                        :error="errors[0]"
                      />
                    </ValidationProvider>
                    <ValidationProvider :name="$t('Language')" rules="required" v-slot="{ errors }">
                      <Select
                        :label="$t('Language')"
                        :placeholder="$t('scheduledReports.selectLanguage')"
                        :options="supportedLanguages"
                        v-model="currentReport.language"
                        :readonly="readonly"
                        :error="errors[0]"
                        searchable
                      />
                    </ValidationProvider>
                  </FormLayoutGroup>
                </FormLayout>
              </CardSection>
              <CardSection v-if="futureDates && futureDates.length">
                <Heading style="margin-bottom: 5px;">{{$t('scheduledReports.nextFiveScheduledRuns')}}</Heading>
                <List>
                  <ListItem v-for="(date, ind) in futureDates" :key="ind">
                    <b>{{date.value}}</b><div class="date-period"> {{$t('scheduledReports.futureDateMessage', { start: date.start, end: date.end })}}</div>
                  </ListItem>
                </List>
              </CardSection>
              <CardSection>
                <FormLayout>
                  <FormLayoutGroup>
                    <ValidationProvider :name="$t('scheduledReports.outputFields')" rules="required" v-slot="{ errors }">
                      <Select
                        :label="$t('scheduledReports.outputFields')"
                        :optionGroups="fields"
                        :placeholder="$t('scheduledReports.selectFields')"
                        v-model="outputFields"
                        :readonly="readonly"
                        multiple
                        :error="errors[0]"
                        searchable
                      />
                    </ValidationProvider>
                  </FormLayoutGroup>
                </FormLayout>
                <!-- <pre>{{outputFields}}</pre> -->
                <draggable-tags
                  v-if="outputFields.length"
                  label=""
                  :help-text="!readonly ? $t('scheduledReports.tagsHelp') : ''"
                  v-model="outputFields"
                  :disabled="readonly"
                />
              </CardSection>
              <CardSection>
                <div :style="{ 'pointer-events': readonly ? 'none' : 'auto', 'cursor' : readonly ? 'not-allowed' : 'auto' }">
                  <div>{{$t('Filters')}}</div>
                  <LoadingLayout 
                    v-if="!filterPanelLoaded" 
                    :card="false" 
                  />
                  <div v-if="filterPanelLoaded && !readonly" class="saved-filter-form">
                    <FormLayout>
                      <FormLayoutGroup>
                        <Select 
                          :placeholder="$t('scheduledReports.selectSavedFilter')"
                          :optionGroups="insightsFilterOptions"
                          v-model="selectedSavedFilterId"
                          :helpText="$t('scheduledReports.selectSavedFilterHelp')"
                          searchable
                        />
                        <Button @click="setFromSavedFilter">{{$t('scheduledReports.setFromSelectedFilter')}}</Button>
                      </FormLayoutGroup>
                    </FormLayout>
                  </div>
                  <InsightsFilterPanel ref="InsightsPanel" v-model="currentReport.filters.ui" @result="filterResult" @loaded="filterLoad" />
                  <!-- <InlineError v-if="!(currentReport.filters.ui && currentReport.filters.ui.length)">{{$t('scheduledReports.insightsFilterRequired')}}</InlineError> -->
                </div>
              </CardSection>
              <CardSection v-if="!showPreview">
                <Button :disabled="cantPreview" @click="loadPreview">{{$t('scheduledReports.previewReport')}}</Button>
              </CardSection>
            </Card>
            <Card 
              v-if="showPreview"
              :title="$t('scheduledReports.preview')"
              :actions="!cantPreview ? [
                {
                  label: $t('scheduledReports.reloadPreview'),
                  onAction: loadPreview
                }
              ] : []"
            >
              <DataTable
                :columns="previewColumns"
                :rows="formattedPreviewData"
                :loading="previewLoading"
              />
              
            </Card>
          </LayoutSection>
        </Layout>
        <!-- <pre>{{currentReport}}</pre> -->
        <!-- <pre>{{previewData}}</pre> -->

      </Page>
    </ValidationObserver>

  </div>
</template>

<script>
import InsightsFilterPanel from '@/insights/InsightsFilterPanel.vue'
import EmailListInput from '../components/EmailListInput'
import InsightsFields from '../../data/insightsFields.js'
import moment from 'moment'
import UI from 'fanplayr-ui'
import _ from 'lodash'
const csv = require('csvtojson')
import DraggableTags from '../components/ui/DraggableTags.vue'


export default {
  name: 'AccountScheduledReportsEdit',
  data () {
    return {
      loading: true,
      saving: false,
      reports: [],
      insightsFilters: [],
      selectedSavedFilterId: null,
      outputFields: [],
      currentReport: null,
      currentReportCopy: null,
      readonly: false,
      previewData: [],
      previewLoading: false,
      showPreview: false
    }
  },
  async mounted() {
    window.INSIGHTS_CONFIG = window.INSIGHTS_CONFIG || {}
    window.INSIGHTS_CONFIG.authKey = await this.api.getSessionKey()

    // this.initFieldsWatcher()

    this.insightsFilters = await this.api.getInsightsFilters()
      .catch(this.apiCatch)


    if (this.$route.name === 'AccountScheduledReportsCreate') {
      this.currentReport = {
        outputFields: [],
        recipients: [],
        filters: {
          ui: [],
          server: []
        }
      }
      this.currentReportCopy = _.cloneDeep(this.currentReport)

      this.loading = false
    } else {
      NProgress.start()
      const id = this.$route.params.reportId
      this.api.getScheduledReport(id)
        .then((data) => {
          this.currentReport = data || {}
          if (!this.currentReport.filters) {
            this.currentReport.filters = {
              ui: [],
              server: []
            }
          }
          this.currentReport.recipients = this.currentReport.recipients || []
          this.currentReportCopy = _.cloneDeep(this.currentReport)
          this.outputFields = (this.currentReport.outputFields || []).map(f => f.field)
          
          if (this.$route.name === 'AccountScheduledReportsEdit') {
            this.readonly = true
          } else if (this.$route.name === 'AccountScheduledReportsCopy') {
            this.currentReport.id = null
          }
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.loading = false
          NProgress.done()
        })
    }
      
  },
  computed: {
    insightsFilterOptions() {
      const rv = []
      const pub = this.insightsFilters.filter(f => f.access === 'public')
      if (pub && pub.length) {
        rv.push({
          title: this.$t('scheduledReports.public'),
          options: pub.map(f => {
            return { value: f.id, label: f.name }
          })
        })
      }
      const priv = this.insightsFilters.filter(f => f.access === 'private')
      if (priv && priv.length) {
        rv.push({
          title: this.$t('scheduledReports.private'),
          options: priv.map(f => {
            return { value: f.id, label: f.name }
          })
        })
      }
      return rv
    },
    previewColumns() {
      if (!(this.previewData && this.previewData.length)) return this.outputFields.map(f => ({ label: this.$t(`scheduledReports.fields.${f}`)}))
      return this.previewData[0].map((d, i) => {
        return {
          key: i.toString(),
          label: d
        }
      })
    },
    formattedPreviewData() {
      if (!(this.previewData && this.previewData.length)) return []
      const data = [].concat(this.previewData)
      data.shift()
      return data.map((row) => {
        const obj = {}
        row.forEach((val, index) => {
          obj[index.toString()] = val
        })
        return obj
      })
    },
    cantSave() {
      return this.saving || 
        this.loading || 
        !this.hasChanged
    },
    cantPreview() {
      return this.saving || 
        this.previewLoading ||
        this.loading || 
        !this.currentReport.period ||
        !this.currentReport.language || 
        !this.outputFields.length
    },
    hasChanged() {
      return !_.isEqual(this.formattedCurrentReport, this.currentReportCopy)
    },
    futureDates() {
      const periodMap = {
        'DAY': 'days',
        'WEEK': 'weeks',
        'MONTH': 'months',
        'QUARTER': 'quarters'
      }
      const dates = []
      if (this.currentReport.period && this.currentReport.scheduleStartDate) {
        let type = 'day'
        const now = moment.utc().tz(this.theUser.accountTimezone)
        let curr = moment.tz(this.currentReport.scheduleStartDate, this.theUser.accountTimezone)
        if (this.currentReport.period === 'WEEK') {
          type = 'week'
          curr = curr.isoWeekday(1)
        } else if (this.currentReport.period === 'MONTH') {
          type = 'month'
          curr = curr.startOf('month')
        } else if (this.currentReport.period === 'QUARTER') {
          type = 'quarter'
          curr = curr.startOf('quarter')
        }
        let i = 0
        while (i < 5) {
          if (curr.isAfter(now)) {
            let start = moment(curr).startOf(type)
            let end = moment(start).endOf(type)
            if (this.currentReport.period === 'DAY') {
              start = start.subtract(1, 'day').startOf('day')
              end = end.subtract(1, 'day').startOf('day')
            } else if (this.currentReport.period === 'WEEK') {
              start = start.subtract(1, 'week').startOf('week').add(1, 'day')
              end = end.subtract(1, 'week').endOf('week').add(1, 'day')
            } else if (this.currentReport.period === 'MONTH') {
              start = start.subtract(1, 'month').startOf('month')
              end = end.subtract(1, 'month').endOf('month')
            } else if (this.currentReport.period === 'QUARTER') {
              start = start.subtract(3, 'months').startOf('quarter')
              end = end.subtract(3, 'months').endOf('quarter')
            }
            // dates.push(this.$t('scheduledReports.futureDateMessage', { 
            //   value: UI.format(curr.format('YYYY-MM-DD'), 'dateLong'),
            //   start: start.format('YYYY/MM/DD'),
            //   end: end.format('YYYY/MM/DD')
            // }))
            dates.push({
              value: UI.format(curr.format('YYYY-MM-DD'), 'dateLong'),
              start: start.format('YYYY/MM/DD'),
              end: end.format('YYYY/MM/DD')
            })
            i++
          }
          curr = curr.add(1, periodMap[this.currentReport.period])
        }
      }
      return dates
    },
    currentGroup() {
      for (const field of this.outputFields) {
        const obj = InsightsFields.find(f => f.field === field)
        if (obj.group) return obj.group
      }
      return null
    },
    fields() {
      return this.groups.map(g => {
        return {
          title: g === 'base' ? this.$t('Basic') : this.$t(`scheduledReports.${g}`),
          options: InsightsFields.filter(f => {
            return (g === 'base' && !f.group) || (g === f.group)
          }).map(f => {
            return {
              label: this.$t(`scheduledReports.fields.${f.field}`),
              value: f.field,
              disabled: this.currentGroup && f.group && this.currentGroup !== f.group || false
            }
          })
        }
      })
    },
    groups() {
      const rawGroups = InsightsFields.filter(f => !!f.group).map(f => f.group)
      const groups = [...new Set(rawGroups)]
      groups.unshift('base')
      return groups
    },
    formattedCurrentReport() {
      return { 
        ...this.currentReport, 
        outputFields: this.outputFields.map(f => {
          return getFieldObject(f)
        })
      }
    },
    periodOptions() {
      return [
        {
          value: 'DAY',
          label: this.$t('Daily')
        },
        {
          value: 'WEEK',
          label: this.$t('Weekly')
        },
        {
          value: 'MONTH',
          label: this.$t('Monthly')
        },
        {
          value: 'QUARTER',
          label: this.$t('Quarterly')
        },
      ]
    }
  },
  methods: {
    cancel() {
      this.$router.push({ name: 'AccountScheduledReportsList' })
    },
    setFromSavedFilter() {
      const selectedSavedFilter = this.insightsFilters.find(f => f.id === this.selectedSavedFilterId)
      if (!(selectedSavedFilter && selectedSavedFilter.json)) return;
      this.$refs.InsightsPanel.updateRulesets(JSON.parse(JSON.stringify(selectedSavedFilter.json.rulesets || [])))
    },
    async loadPreview() {
      if (!this.showPreview) {
        const confirm = await this.$ui.confirm({
          title: this.$t('scheduledReports.loadPreview'),
          content: this.$t('scheduledReports.loadPreviewMessage'),
          primaryActionLabel: this.$t('Start'),
          primaryActionType: 'primary'
        })
        if (!confirm) {
          return 
        }
      }
      this.previewData = []
      this.previewLoading = true
      this.showPreview = true
      this.api.loadScheduledReportPreview(this.formattedCurrentReport)
        .then(async (data) => {
          this.previewData = await csv({
            noheader:true,
            output: "csv"
          }).fromString(data)
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.previewLoading = false
        })

    },
    saveReport() {
      this.saving = true
      NProgress.start()
      if (this.formattedCurrentReport.id) {
        this.api.updateScheduledReport(this.formattedCurrentReport)
          .then(() => {
            this.$ui.toast({ title: this.$t('Saved') })
            this.$router.push({ name: 'AccountScheduledReportsList' })
          })
          .catch(this.apiCatch)
          .finally(() => {
            this.saving = false
            NProgress.done()
          })
      } else {
        this.api.createScheduledReport(this.formattedCurrentReport)
          .then(() => {
            this.$ui.toast({ title: this.$t('Saved') })
            this.$router.push({ name: 'AccountScheduledReportsList' })
          })
          .catch(this.apiCatch)
          .finally(() => {
            this.saving = false
            NProgress.done()
          })
      }
    },
    filterResult(serverRulesets) {
      this.filterPanelValid = !!serverRulesets
      if (this.filterPanelValid) {
        this.currentReport.filters.server = serverRulesets
      }
    },
    filterLoad() {
      console.log('loaded insights')
      this.filterPanelLoaded = true
    }
  },
  components: {
    EmailListInput,
    InsightsFilterPanel,
    DraggableTags,
  }
}
function getFieldObject(fieldKey) {
  return InsightsFields.find(f => f.field == fieldKey)
}
</script>

<style lang="scss">
#AccountScheduledReportsEdit {
  .date-period {
    display: inline; 
    font-size: 13px; 
    color: #637381;
  }
  .saved-filter-form {
    margin: 10px 0px;
  }
}
</style>
